import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import EastOutlinedIcon from "@mui/icons-material/EastOutlined";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import LaunchOutlinedIcon from "@mui/icons-material/LaunchOutlined";
import usePersistentPayerCode from "../../hooks/usePersistentPayerCode";

type ComponentRow = {
  code: string;
  codeColor: string;
  name: string;
  description: string;
  ig: string;
  version: string;
  url: string;
  versionPillBg: string;
  versionPillText: string;
  rowBg: string;
  rowHover: string;
};

const componentRows: ComponentRow[] = [
  {
    code: "CRD",
    codeColor: "text-(--darkblue)",
    name: "Coverage Requirements Discovery",
    description:
      "Tells the provider if prior authorization is required, in real time, at the point of care",
    ig: "Da Vinci CRD",
    version: "2.2.1",
    url: "http://hl7.org/fhir/us/davinci-crd",
    versionPillBg: "bg-[#e5ebff]",
    versionPillText: "text-(--darkblue)",
    rowBg: "bg-white",
    rowHover: "hover:bg-[#f7faff]",
  },
  {
    code: "DTR",
    codeColor: "text-(--moderatevioletcustom)",
    name: "Documentation Templates & Rules",
    description:
      "Guides the provider to collect and submit the exact clinical information the payer requires",
    ig: "Da Vinci DTR",
    version: "2.1.0",
    url: "http://hl7.org/fhir/us/davinci-dtr",
    versionPillBg: "bg-[#f7e6ff]",
    versionPillText: "text-(--moderatevioletcustom)",
    rowBg: "bg-[#f7faff]",
    rowHover: "hover:bg-[#eef3fb]",
  },
  {
    code: "PAS",
    codeColor: "text-(--darkblue)",
    name: "Prior Authorization Support",
    description:
      "Electronically submits the authorization request and returns the payer's decision",
    ig: "Da Vinci PAS",
    version: "2.1.0",
    url: "http://hl7.org/fhir/us/davinci-pas",
    versionPillBg: "bg-[#e5ebff]",
    versionPillText: "text-(--darkblue)",
    rowBg: "bg-white",
    rowHover: "hover:bg-[#f7faff]",
  },
  {
    code: "CDEX",
    codeColor: "text-(--moderatevioletcustom)",
    name: "Prior Authorization Attachment",
    description:
      "Electronically submits the authorization request and returns the payer's decision",
    ig: "Da Vinci CDEX",
    version: "2.1.0",
    url: "https://hl7.org/fhir/us/davinci-cdex",
    versionPillBg: "bg-[#f7e6ff]",
    versionPillText: "text-(--moderatevioletcustom)",
    rowBg: "bg-[#f7faff]",
    rowHover: "hover:bg-[#eef3fb]",
  },
];

const PriorAuthApi = () => {
  usePersistentPayerCode();

  return (
    <div className="page-layout api-layout overflow-x-hidden!">
      <div className="flex flex-col gap-6">
        {/* Hero */}
        <div className="relative rounded-2xl p-6 md:p-8 text-white bg-[linear-gradient(120deg,#041e79_0%,#0331a4_45%,#8c32b7_100%)] shadow-[0_12px_30px_rgba(4,30,121,0.18)]">
          <div className="pointer-events-none absolute -top-16 -right-16 h-56 w-56 rounded-full bg-[radial-gradient(circle,rgba(255,255,255,0.18)_0%,rgba(255,255,255,0)_70%)]" />
          <div className="relative flex flex-col gap-6 justify-between md:flex-row md:items-center">
            <div className="flex flex-row items-center gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-white/15 backdrop-blur-sm">
                <AssignmentTurnedInOutlinedIcon fontSize="large" />
              </div>
              <div>
                <p className="m-0 text-xs font-semibold uppercase opacity-85">
                  Prior Authorization API
                </p>
                <h2 className="m-0 mt-1 text-2xl font-bold leading-tight text-white normal-case">
                  Developer Guide
                </h2>
              </div>
            </div>

            <div className="flex h-6 items-center rounded-full border border-white/25 bg-white/15 px-2.5 text-xs font-semibold text-white w-max">
              FHIR ENABLED
            </div>
          </div>
        </div>

        {/* Product Overview */}
        <div className="relative rounded-2xl border border-[#e3e7f3] bg-white shadow-[0_4px_12px_rgba(4,30,121,0.04)]">
          <div className="pointer-events-none absolute top-1 right-0 h-56 w-56 rounded-full bg-[radial-gradient(circle,rgba(140,50,183,0.08)_0%,rgba(140,50,183,0)_70%)]" />

          <div className="relative p-5 md:p-7">
            <div className="flex flex-row items-center gap-3 mb-6">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-[#f7e6ff] text-(--moderatevioletcustom)">
                <MenuBookOutlinedIcon fontSize="small" />
              </div>
              <div>
              
                <h3 className="m-0 mt-0.5 text-lg font-semibold normal-case">
                  Product Overview
                </h3>
              </div>
            </div>

            {/* Problem / Solution split */}
            <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div className="relative rounded-xl border border-[#e3e7f3] bg-[linear-gradient(135deg,#fbf5ff_0%,#f4e8fc_100%)] p-5">
                <div className="mb-3 flex flex-row items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white text-(--moderatevioletcustom) shadow-[0_2px_6px_rgba(140,50,183,0.15)]">
                    <HourglassEmptyOutlinedIcon fontSize="small" />
                  </div>
                  <p className="m-0 text-xs font-semibold uppercase tracking-widest">
                    The Challenge
                  </p>
                </div>
                <p className="m-0 text-sm tracking-wide">
                  Prior authorization is essential to confirm that a proposed
                  treatment or procedure is medically appropriate. But the
                  traditional process has long relied on phone calls, faxes,
                  and manual portals &mdash; creating delays that impact both
                  providers and patients.
                </p>
              </div>

              <div className="relative rounded-xl border border-[#e3e7f3] bg-[linear-gradient(135deg,#f5f8ff_0%,#e5ebff_100%)] p-5">
                <div className="mb-3 flex flex-row items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white text-(--darkblue) shadow-[0_2px_6px_rgba(4,30,121,0.15)]">
                    <LightbulbOutlinedIcon fontSize="small" />
                  </div>
                  <p className="m-0 text-xs font-bold uppercase tracking-widest">
                    The Solution
                  </p>
                </div>
                <p className="m-0 text-sm tracking-wide">
                  The CMS Interoperability and Prior Authorization Final Rule
                  requires health plans to expose a standards-based,
                  FHIR-powered API &mdash; enabling providers and payers to
                  exchange coverage requirements, documentation needs, and
                  decisions electronically and in near real time.
                </p>
              </div>
            </div>

            {/* Compliance banner */}
            <div className="flex flex-col gap-3 rounded-xl border border-[#e3e7f3] bg-[linear-gradient(120deg,#ffffff_0%,#e5ebff_100%)] p-4 md:flex-row md:items-center md:justify-between">
              <div className="flex flex-row items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-(--darkblue) text-white shadow-[0_4px_10px_rgba(4,30,121,0.25)]">
                  <GavelOutlinedIcon fontSize="small" />
                </div>
                <div>
                  <p className="m-0 text-[10px] font-semibold uppercase tracking-widest">
                    Regulatory Mandate
                  </p>
                  <p className="m-0 mt-0.5 text-sm font-semibold">
                    CMS Interoperability and Prior Authorization Final Rule
                  </p>
                </div>
              </div>
              <span className="inline-flex w-max items-center gap-1.5 rounded-full bg-(--darkblue) px-3 py-1.5 text-xs font-bold text-white shadow-[0_4px_10px_rgba(4,30,121,0.25)]">
                <span className="h-1.5 w-1.5 rounded-full bg-white" />
                CMS-0057-F
              </span>
            </div>
          </div>
        </div>

        {/* Product Description */}
        <div className="relative rounded-2xl border border-[#e3e7f3] bg-white shadow-[0_4px_12px_rgba(4,30,121,0.04)]">
          <div className="p-5 md:p-7">
            <div className="flex flex-row items-center gap-3 mb-5">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-[#e5ebff] text-(--darkblue)">
                <VerifiedOutlinedIcon fontSize="small" />
              </div>
              <div>
                <h3 className="m-0 mt-0.5 text-lg font-semibold normal-case">
                  Product Description
                </h3>
              </div>
            </div>

            <p className="m-0 mb-6 text-sm">
              The Da Vinci Prior Authorization workflow is powered by three
              complementary FHIR implementation guides that together support
              the full lifecycle &mdash; from checking coverage requirements
              through submitting the request and receiving a decision.
            </p>

            {/* Workflow stepper */}
            <p className="m-0 mb-3 text-xs font-semibold uppercase tracking-widest">
              Workflow
            </p>
            <div className="mb-6 flex flex-col items-stretch gap-3 md:flex-row md:items-stretch">
              <div className="flex flex-1 flex-row items-center gap-3 rounded-xl border border-[#e3e7f3] bg-white p-4 shadow-[0_4px_12px_rgba(4,30,121,0.06)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_20px_rgba(4,30,121,0.1)]">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-[#e5ebff] text-(--darkblue)">
                  <FactCheckOutlinedIcon fontSize="small" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="m-0 text-sm font-semibold">
                    CRD
                  </p>
                  <p className="m-0 mt-0.5 text-xs">
                    Coverage Requirements Discovery
                  </p>
                </div>
              </div>

              <EastOutlinedIcon
                className="hidden self-center text-(--moderatevioletcustom) md:block"
                fontSize="small"
              />

              <div className="flex flex-1 flex-row items-center gap-3 rounded-xl border border-[#e3e7f3] bg-white p-4 shadow-[0_4px_12px_rgba(4,30,121,0.06)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_20px_rgba(4,30,121,0.1)]">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-[#f7e6ff] text-(--moderatevioletcustom)">
                  <EditNoteOutlinedIcon fontSize="small" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="m-0 text-sm font-semibold">
                    DTR
                  </p>
                  <p className="m-0 mt-0.5 text-xs">
                    Documentation Templates &amp; Rules
                  </p>
                </div>
              </div>

              <EastOutlinedIcon
                className="hidden self-center text-(--moderatevioletcustom) md:block"
                fontSize="small"
              />

              <div className="flex flex-1 flex-row items-center gap-3 rounded-xl border border-[#e3e7f3] bg-white p-4 shadow-[0_4px_12px_rgba(4,30,121,0.06)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_20px_rgba(4,30,121,0.1)]">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-[#e5ebff] text-(--darkblue)">
                  <TaskAltOutlinedIcon fontSize="small" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="m-0 text-sm font-semibold">
                    PAS
                  </p>
                  <p className="m-0 mt-0.5 text-xs">
                    Prior Authorization Support
                  </p>
                </div>
              </div>
            </div>

            {/* Component detail table */}
            <p className="m-0 mb-3 text-xs font-bold uppercase tracking-widest text-(--moderatevioletcustom)">
              Component Details
            </p>

            {/* Desktop / tablet table */}
            <div className="hidden md:block rounded-xl border border-[#e3e7f3] shadow-[0_4px_12px_rgba(4,30,121,0.06)]">
              <div className="overflow-x-auto rounded-xl">
                <table className="w-full border-collapse text-sm rounded-xl">
                  <thead>
                    <tr className="bg-(--darkblue) text-white">
                      <th className="px-4 py-3 text-left font-semibold whitespace-nowrap">
                        Component
                      </th>
                      <th className="px-4 py-3 text-left font-semibold">
                        What It Does
                      </th>
                      <th className="px-4 py-3 text-left font-semibold whitespace-nowrap">
                        Da Vinci IG
                      </th>
                      <th className="px-4 py-3 text-left font-semibold whitespace-nowrap">
                        Version
                      </th>
                      <th className="px-4 py-3 text-left font-semibold">
                        URL
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {componentRows.map((row, i) => (
                      <tr
                        key={row.code}
                        className={`${row.rowBg} ${row.rowHover} transition-colors duration-150 ${i > 0 ? "border-t border-[#e3e7f3]" : ""}`}
                      >
                        <td className="px-4 py-3 align-top">
                          <span className="font-semibold">
                            {row.code}
                          </span>{" "}
                          <span className="text-(--desaturateddarkblue)">
                            {row.name}
                          </span>
                        </td>
                        <td className="px-4 py-3 align-top text-(--desaturateddarkblue)">
                          {row.description}
                        </td>
                        <td className="px-4 py-3 align-top text-(--desaturateddarkblue) whitespace-nowrap">
                          {row.ig}
                        </td>
                        <td className="px-4 py-3 align-top whitespace-nowrap">
                          <span
                            className={`inline-flex items-center rounded-full ${row.versionPillBg} px-2.5 py-0.5 text-xs font-semibold ${row.versionPillText}`}
                          >
                            {row.version}
                          </span>
                        </td>
                        <td className="px-4 py-3 align-top">
                          <a
                            href={row.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={`Open ${row.ig} implementation guide`}
                            title={row.url}
                            className="inline-flex items-center text-(--moderatevioletcustom) no-underline transition-opacity duration-200 hover:opacity-80"
                          >
                            <LaunchOutlinedIcon fontSize="small" />
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile card list */}
            <div className="flex flex-col gap-3 md:hidden">
              {componentRows.map((row) => (
                <div
                  key={row.code}
                  className="rounded-xl border border-[#e3e7f3] bg-white shadow-[0_4px_12px_rgba(4,30,121,0.06)]"
                >
                  <div className="flex flex-row rounded-t-xl items-start justify-between gap-3 bg-(--darkblue) p-3 text-white">
                    <div className="min-w-0 flex-1">
                      <p
                        className={`m-0 text-xs font-bold uppercase tracking-widest ${row.codeColor === "text-(--moderatevioletcustom)" ? "text-[#f7e6ff]" : "text-[#c7d4ff]"}`}
                      >
                        {row.code}
                      </p>
                      <p className="m-0 mt-0.5 text-sm font-semibold text-white">
                        {row.name}
                      </p>
                    </div>
                    <span
                      className={`inline-flex shrink-0 items-center rounded-full ${row.versionPillBg} px-2.5 py-0.5 text-xs font-semibold ${row.versionPillText}`}
                    >
                      {row.version}
                    </span>
                  </div>
                  <div className="flex flex-col gap-3 p-3">
                    <div className="flex flex-col gap-1">
                      <span className="text-[10px] font-bold uppercase tracking-widest text-(--moderatevioletcustom)">
                        What It Does
                      </span>
                      <p className="m-0 text-sm text-(--desaturateddarkblue)">
                        {row.description}
                      </p>
                    </div>
                    <div className="flex flex-row items-center justify-between gap-3 border-t border-[#e3e7f3] pt-3">
                      <div className="flex flex-col gap-1">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-(--moderatevioletcustom)">
                          Da Vinci IG
                        </span>
                        <p className="m-0 text-sm font-semibold text-(--desaturateddarkblue)">
                          {row.ig}
                        </p>
                      </div>
                      <a
                        href={row.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Open ${row.ig} implementation guide`}
                        title={row.url}
                        className="inline-flex shrink-0 items-center text-(--moderatevioletcustom) no-underline transition-opacity duration-200 hover:opacity-80"
                      >
                        <LaunchOutlinedIcon fontSize="small" />
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Closing summary */}
            <div className="mt-6 rounded-xl border border-[#e3e7f3] bg-[linear-gradient(120deg,#ffffff_0%,#f7e6ff_60%,#e5ebff_100%)] p-4 md:p-5">
              <div className="flex flex-row items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white text-(--moderatevioletcustom) shadow-[0_4px_10px_rgba(140,50,183,0.15)]">
                  <HubOutlinedIcon fontSize="small" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="m-0 text-[10px] font-bold uppercase tracking-widest">
                    The Complete Picture
                  </p>
                  <p className="m-0 mt-1 text-sm">
                    Together,{" "}
                    <strong>CRD</strong>,{" "}
                    <strong>
                      DTR
                    </strong>
                    {" "} and{" "}
                    <strong>PAS</strong>
                    {" "}
                    replace the fragmented, manual prior authorization process
                    with a connected, automated workflow &mdash; embedded
                    directly inside the provider&apos;s EHR.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Implementation Guide CTA */}

      </div>
    </div>
  );
};

export default PriorAuthApi;
